"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  contactEndpoint,
  contactFormSchema,
  contactTopics,
  type ContactFormInput,
} from "@/features/contact/schema";
import { cn } from "@/lib/utils";

const fieldClass =
  "w-full rounded-lg border border-line bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted transition-colors focus:border-green/60 focus:outline-none focus:ring-2 focus:ring-green/20";

type Status = "idle" | "submitting" | "error" | "success";

async function readProxyMessage(response: Response): Promise<string | undefined> {
  try {
    const data: unknown = await response.json();
    if (data && typeof data === "object" && "message" in data) {
      const { message } = data as { message?: unknown };
      if (typeof message === "string" && message.trim() !== "") return message;
    }
  } catch {
    // Proxy může vracet i HTML (klasické PHP zpracování) — zprávu pak nemáme.
  }
  return undefined;
}

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormInput>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      phone: "",
      topic: contactTopics[0],
      message: "",
      consent: false,
      website: "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setStatus("submitting");
    setFeedback(null);

    // Jednoduchý honeypot: skryté pole plní pouze roboti.
    if (values.website && values.website.trim() !== "") {
      setStatus("success");
      setFeedback("Děkujeme za poptávku. Ozveme se co nejdříve.");
      reset();
      return;
    }

    const body = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      body.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
    });

    try {
      const response = await fetch(contactEndpoint, {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body,
      });

      const message = await readProxyMessage(response);

      if (!response.ok) {
        setStatus("error");
        setFeedback(
          message ??
            "Poptávku se nepodařilo odeslat. Zkuste to prosím znovu nebo nám zavolejte.",
        );
        return;
      }

      setStatus("success");
      setFeedback(message ?? "Děkujeme za poptávku. Ozveme se co nejdříve.");
      reset();
    } catch {
      setStatus("error");
      setFeedback("Odeslání se nezdařilo. Zkuste to prosím znovu nebo nám zavolejte.");
    }
  });

  if (status === "success") {
    return (
      <div
        role="status"
        className="flex h-full flex-col items-center justify-center gap-4 rounded-2xl border border-green/25 bg-white p-10 text-center shadow-card"
      >
        <span className="inline-flex size-14 items-center justify-center rounded-full bg-green-soft text-green">
          <CheckCircle2 className="size-7" aria-hidden="true" />
        </span>
        <h3 className="text-xl font-semibold text-blue-dark">Poptávka odeslána</h3>
        <p className="max-w-sm text-sm leading-relaxed text-ink-soft">{feedback}</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-dark"
        >
          Poslat další poptávku
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      action={contactEndpoint}
      method="post"
      noValidate
      className="rounded-2xl border border-line bg-white p-6 shadow-card sm:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="name" className="text-sm font-medium text-ink">
            Jméno a příjmení <span className="text-green">*</span>
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? "name-error" : undefined}
            className={cn(fieldClass, errors.name && "border-red-400")}
            {...register("name")}
          />
          {errors.name ? (
            <p id="name-error" className="text-xs text-red-600">
              {errors.name.message}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="company" className="text-sm font-medium text-ink">
            Firma
          </label>
          <input
            id="company"
            type="text"
            autoComplete="organization"
            className={fieldClass}
            {...register("company")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium text-ink">
            E-mail <span className="text-green">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? "email-error" : undefined}
            className={cn(fieldClass, errors.email && "border-red-400")}
            {...register("email")}
          />
          {errors.email ? (
            <p id="email-error" className="text-xs text-red-600">
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="phone" className="text-sm font-medium text-ink">
            Telefon
          </label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            className={fieldClass}
            {...register("phone")}
          />
        </div>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label htmlFor="topic" className="text-sm font-medium text-ink">
            Téma poptávky
          </label>
          <select id="topic" className={fieldClass} {...register("topic")}>
            {contactTopics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label htmlFor="message" className="text-sm font-medium text-ink">
            Zpráva <span className="text-green">*</span>
          </label>
          <textarea
            id="message"
            rows={5}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={errors.message ? "message-error" : undefined}
            placeholder="Popište prosím váš projekt nebo poptávku…"
            className={cn(fieldClass, "resize-y", errors.message && "border-red-400")}
            {...register("message")}
          />
          {errors.message ? (
            <p id="message-error" className="text-xs text-red-600">
              {errors.message.message}
            </p>
          ) : null}
        </div>
      </div>

      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">Web</label>
        <input
          id="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register("website")}
        />
      </div>

      <div className="mt-5 flex flex-col gap-1.5">
        <label htmlFor="consent" className="flex items-start gap-3 text-sm text-ink-soft">
          <input
            id="consent"
            type="checkbox"
            aria-invalid={errors.consent ? true : undefined}
            aria-describedby={errors.consent ? "consent-error" : undefined}
            className="mt-0.5 size-4 shrink-0 rounded border-line text-green accent-[#4c8700]"
            {...register("consent")}
          />
          <span>
            Souhlasím se zpracováním osobních údajů za účelem vyřízení poptávky.
          </span>
        </label>
        {errors.consent ? (
          <p id="consent-error" className="text-xs text-red-600">
            {errors.consent.message}
          </p>
        ) : null}
      </div>

      {status === "error" && feedback ? (
        <p
          role="alert"
          className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {feedback}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_-12px_rgba(76,135,0,0.8)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-dark disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Odesílám…
          </>
        ) : (
          <>
            Odeslat poptávku
            <Send className="size-4" aria-hidden="true" />
          </>
        )}
      </button>
    </form>
  );
}
